import type { AccountOption } from "@app/components/account-combobox";
import KpiChart from "@app/components/kpi-chart";
import type { StakeholderOption } from "@app/components/stakeholder-combobox";
import TxnTablePanel, { useTxnSort } from "@app/components/txn-table-panel";
import { useTxnBulkActions } from "@app/components/txn-table-panel/use-txn-bulk-actions";
import { useTxnPanelRefresh } from "@app/components/txn-table-panel/use-txn-panel-refresh";
import { getPerspectiveId } from "@app/lib/perspective";
import CategoryCell, { type CategoryOption } from "@app/pages/transactions/category-cell";
import { environment } from "@app/relay/relay-environment";
import { Link } from "@tanstack/react-router";
import { Suspense, useEffect, useState } from "react";
import {
  commitMutation,
  fetchQuery,
  graphql,
  useLazyLoadQuery,
  usePreloadedQuery,
} from "react-relay";
import type { PreloadedQuery } from "react-relay";

import type { stakeholderDetailPage_Query } from "@app/queries/stakeholderDetailPage_Query.graphql";
import type { stakeholderDetailPageTxns_Query } from "@app/queries/stakeholderDetailPageTxns_Query.graphql";
import type { stakeholderDetailPage_ResetStakeholderMutation } from "@app/queries/stakeholderDetailPage_ResetStakeholderMutation.graphql";
import type { stakeholderDetailPage_TransformChainSetMutation } from "@app/queries/stakeholderDetailPage_TransformChainSetMutation.graphql";
import type { stakeholderDetailPage_TransformWandApplyMutation } from "@app/queries/stakeholderDetailPage_TransformWandApplyMutation.graphql";
import TransformChainSection, { type DraftStep } from "./transform-chain-section";
import TransformAnchorDialog, { type WandArgs } from "./transform-anchor-dialog";

// The chrome query: this stakeholder's normalization replay, rules, monthly flow,
// breadcrumb/category, plus the category catalog the inline CategoryCell needs. The
// txn list now lives on the unified `transactions` resolver (see StakeholderTxns).
export const stakeholderDetailQuery = graphql`
  query stakeholderDetailPage_Query(
    $stakeholderId: ID!
    $month: String
    $perspectiveId: ID
    $from: String
  ) {
    stakeholderDetail(
      stakeholderId: $stakeholderId
      month: $month
      perspectiveId: $perspectiveId
      from: $from
    ) {
      stakeholder {
        id
        name
        morphSteps {
          kind
          label
          total
          delta
          points {
            month
            value
          }
        }
        transformChain {
          enabled
          steps {
            ordinal
            kind
            paramsJson
            anchorJson
          }
        }
      }
      path
      category
      rules {
        id
        regex
        direction
      }
      monthly {
        month
        cashIn
        cashOut
      }
    }
    categories(perspectiveId: $perspectiveId) {
      id
      name
      parent {
        id
      }
    }
    transformCatalog {
      kind
      paramsSchemaJson
      conservesExactly
    }
  }
`;

// The editable txn list, pulled off the unified `transactions` resolver
// (counterpartyId drill, optionally scoped to the month clicked on the flow chart),
// plus the option lists the bulk-action registry needs. categoryNode { id } is
// selected for the inline CategoryCell (it edits by category id).
const txnsQuery = graphql`
  query stakeholderDetailPageTxns_Query(
    $counterpartyId: ID
    $month: String
    $perspectiveId: ID
    $sort: TxnSortEnum
    $sortDirection: String
  ) {
    transactions(
      counterpartyId: $counterpartyId
      month: $month
      perspectiveId: $perspectiveId
      sort: $sort
      sortDirection: $sortDirection
    ) {
      totalCount
      totalAmount
      limit
      txns {
        txn {
          id
        }
        md5
        postingDate
        description
        amount
        kind
        accountNumber
        internal
        classification
        counterparty {
          name
        }
        category
        categoryNode {
          id
          name
        }
        expenseNature {
          id
          name
        }
      }
    }
    allStakeholders {
      id
      name
    }
    bankAccounts {
      id
      name
      nickname
      last4
    }
    expenseNatures(perspectiveId: $perspectiveId) {
      id
      name
    }
  }
`;

const transformWandApplyMutation = graphql`
  mutation stakeholderDetailPage_TransformWandApplyMutation(
    $input: TransformWandApplyInputObject!
  ) {
    transformWandApply(input: $input) {
      stakeholder {
        id
        transformChain {
          enabled
          steps {
            ordinal
            kind
            paramsJson
            anchorJson
          }
        }
      }
      errors {
        message
      }
    }
  }
`;

const transformChainSetMutation = graphql`
  mutation stakeholderDetailPage_TransformChainSetMutation(
    $input: TransformChainSetInputObject!
  ) {
    transformChainSet(input: $input) {
      stakeholder {
        id
        transformChain {
          enabled
          steps {
            ordinal
            kind
            paramsJson
            anchorJson
          }
        }
      }
      errors {
        message
      }
    }
  }
`;

const resetStakeholderMutation = graphql`
  mutation stakeholderDetailPage_ResetStakeholderMutation(
    $input: ResetStakeholderInputObject!
  ) {
    resetStakeholder(input: $input) {
      stakeholder {
        id
      }
      errors {
        message
      }
    }
  }
`;

const money = (value: number) =>
  value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  });

type MorphStep =
  stakeholderDetailPage_Query["response"]["stakeholderDetail"]["stakeholder"]["morphSteps"][number];

// The plumbing nodes (Root + per-account BASE) are never valid counterparties.
const isRealStakeholder = (s: { name: string }) =>
  !s.name.startsWith("&") &&
  !s.name.toLowerCase().includes("root stakeholder") &&
  s.name !== "_root";

// The U8 replay: a collapsible re-derivation of this stakeholder's normalization
// chain (cash genesis → normalized terminal), shown between the rules section and
// the flow chart. Three states (U3 makes the conservation-violation state
// impossible, so it is not rendered): errored (the chain raised), trivial (no
// transforms — normalized equals cash), and a real chain (cash vs normalized, with
// the redistributed amount called out).
function NormalizationReplaySection({ steps }: { steps: readonly MorphStep[] }) {
  const [open, setOpen] = useState(false);

  const errored = steps.find((step) => step.kind === "ERROR");
  const genesis = steps.find((step) => step.kind === "GENESIS");
  const terminal = steps.find((step) => step.kind === "TERMINAL");
  // Trivial when there is no terminal (single-slide structural/empty chain) or the
  // terminal redistributed nothing (a no-op passthrough) — normalized == cash.
  const trivial = !errored && (!terminal || terminal.delta === 0);

  const months = Array.from(
    new Set([
      ...(genesis?.points.map((p) => p.month) ?? []),
      ...(terminal?.points.map((p) => p.month) ?? []),
    ])
  ).sort();
  const seriesFor = (step: MorphStep | undefined) => {
    const byMonth = new Map(step?.points.map((p) => [p.month, p.value]) ?? []);
    return months.map((month) => ({ month, value: byMonth.get(month) ?? 0 }));
  };

  return (
    <section className="bg-white rounded border">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between p-4"
      >
        <h2 className="font-bold text-sm">Normalization replay</h2>
        <span className="text-gray-400 text-xs">{open ? "Hide ▾" : "Show ▸"}</span>
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-3">
          {errored ? (
            <p className="text-sm text-amber-700">{errored.label}</p>
          ) : steps.length === 0 ? (
            <p className="text-xs text-gray-400">
              No normalization chain has been built yet.
            </p>
          ) : trivial ? (
            <p className="text-sm text-gray-500">
              No transforms applied — normalized equals cash.
            </p>
          ) : (
            <>
              <p className="text-sm text-gray-600">
                Normalized smooths {money(terminal?.delta ?? 0)} of cost across the
                months it covers; the annual cost is unchanged ({money(genesis?.total ?? 0)}
                ).
              </p>
              <KpiChart
                title="Cash vs normalized (re-derived)"
                series={[
                  {
                    name: "Cash (genesis)",
                    type: "bar",
                    color: "#94a3b8",
                    points: seriesFor(genesis),
                  },
                  {
                    name: "Normalized (terminal)",
                    type: "bar",
                    color: "#7c3aed",
                    points: seriesFor(terminal),
                  },
                ]}
                format="money"
                onMonthClick={() => {}}
              />
            </>
          )}
        </div>
      )}
    </section>
  );
}

export default function StakeholderDetailPage({
  queryRef,
  stakeholderId,
  month,
  onMonthChange,
}: {
  queryRef: PreloadedQuery<stakeholderDetailPage_Query>;
  stakeholderId: string;
  month: string | undefined;
  onMonthChange: (month: string | undefined) => void;
}) {
  const preloaded = usePreloadedQuery(stakeholderDetailQuery, queryRef);
  const [data, setData] = useState(preloaded);
  useEffect(() => setData(preloaded), [preloaded]);

  const [confirmingReset, setConfirmingReset] = useState(false);
  const [resetting, setResetting] = useState(false);

  const resetStakeholder = () => {
    setResetting(true);
    commitMutation<stakeholderDetailPage_ResetStakeholderMutation>(environment, {
      mutation: resetStakeholderMutation,
      variables: { input: { stakeholderId } },
      onCompleted: () => {
        setResetting(false);
        setConfirmingReset(false);
        refreshInPlace();
      },
      onError: (err) => {
        setResetting(false);
        window.alert(err.message);
      },
    });
  };

  // Refetch the exact chrome operation shown (same variables) so a category edit
  // persists instead of being reverted by the preloaded→data sync.
  const refreshInPlace = () => {
    fetchQuery<stakeholderDetailPage_Query>(
      environment,
      stakeholderDetailQuery,
      queryRef.variables
    )
      .toPromise()
      .then((fresh) => fresh && setData(fresh));
  };

  // Save the whole ordered chain through the single write path; the backend
  // validates conservation + anchors and rejects an invalid chain (surfaced in the
  // editor). On success refetch so the chain + the replay preview reflect it.
  const saveChain = (steps: DraftStep[]) =>
    new Promise<void>((resolve, reject) => {
      commitMutation<stakeholderDetailPage_TransformChainSetMutation>(environment, {
        mutation: transformChainSetMutation,
        variables: { input: { stakeholderId, steps } },
        onCompleted: (response, errors) => {
          const message =
            errors?.[0]?.message ??
            response.transformChainSet?.errors?.[0]?.message;
          if (message) {
            reject(new Error(message));
          } else {
            refreshInPlace();
            resolve();
          }
        },
        onError: (err) => reject(err),
      });
    });

  const detail = data.stakeholderDetail;
  const categories = data.categories;

  const series = [
    {
      name: "Money in",
      type: "bar" as const,
      stack: "flow",
      color: "#0f766e",
      points: detail.monthly.map((m) => ({ month: m.month, value: m.cashIn })),
    },
    {
      name: "Money out",
      type: "bar" as const,
      stack: "flow",
      color: "#dc2626",
      points: detail.monthly.map((m) => ({ month: m.month, value: m.cashOut })),
    },
  ];

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4">
      <div>
        <p className="text-xs text-gray-400">{detail.path.join(" → ")}</p>
        <div className="flex items-baseline gap-3">
          <h1 className="text-xl font-bold">{detail.stakeholder.name}</h1>
          {detail.category ? (
            <span className="text-xs bg-emerald-50 text-emerald-700 rounded px-1.5 py-0.5">
              {detail.category}
            </span>
          ) : (
            <span className="text-xs text-amber-600">
              no category — assign one on /stakeholders
            </span>
          )}
          <button
            type="button"
            onClick={() => setConfirmingReset(true)}
            className="ml-auto text-xs px-2 py-1 rounded border border-red-300 text-red-700 hover:bg-red-50"
            title="Un-attach all transactions and delete all rules for this stakeholder"
          >
            Reset stakeholder
          </button>
        </div>
      </div>

      <section className="bg-white rounded border p-4 space-y-1.5">
        <h2 className="font-bold text-sm">Categorization rules</h2>
        {detail.rules.length === 0 ? (
          <p className="text-xs text-gray-400">
            No learned rules yet — confirming a suggestion on the review page
            creates one, after which matching transactions resolve here
            automatically on every future import.
          </p>
        ) : (
          <ul className="text-sm space-y-1">
            {detail.rules.map((rule, i) => (
              <li key={i}>
                <Link
                  to="/rules/$ruleId"
                  params={{ ruleId: rule.id }}
                  className="hover:underline"
                >
                  <span className="text-gray-400 text-xs">
                    {rule.direction === "ANY"
                      ? "regex"
                      : rule.direction.toLowerCase()}
                  </span>{" "}
                  <code className="font-mono text-xs bg-gray-50 rounded px-1 py-0.5 cursor-pointer">
                    {rule.regex}
                  </code>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      <NormalizationReplaySection steps={detail.stakeholder.morphSteps} />

      <TransformChainSection
        steps={detail.stakeholder.transformChain.steps}
        catalog={data.transformCatalog}
        onSave={saveChain}
      />

      <KpiChart
        title="Monthly flow with this counterparty — click a month to filter"
        series={series}
        format="money"
        onMonthClick={(clicked) =>
          onMonthChange(clicked === month ? undefined : clicked)
        }
      />

      {month && (
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <button
            type="button"
            onClick={() => onMonthChange(undefined)}
            className="rounded border px-2 py-0.5 text-gray-600"
          >
            {month} ✕
          </button>
        </div>
      )}

      <div className="bg-white rounded border overflow-hidden">
        <Suspense
          fallback={<p className="p-6 text-sm text-gray-400">Loading transactions…</p>}
        >
          <StakeholderTxns
            stakeholderId={stakeholderId}
            month={month}
            categories={categories}
            onChrome={refreshInPlace}
          />
        </Suspense>
      </div>

      {confirmingReset && (
        <div
          className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center"
          onClick={() => !resetting && setConfirmingReset(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl p-5 max-w-md space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-sm font-semibold text-gray-900">
              Reset {detail.stakeholder.name}?
            </h2>
            <p className="text-sm text-gray-600">
              This un-attaches every transaction from{" "}
              <span className="font-medium">{detail.stakeholder.name}</span>{" "}
              (returning them to unattributed) and deletes all{" "}
              {detail.rules.length} of its categorization rule
              {detail.rules.length === 1 ? "" : "s"}. The stakeholder itself is
              kept. This cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmingReset(false)}
                disabled={resetting}
                className="text-sm px-3 py-1.5 rounded border hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={resetStakeholder}
                disabled={resetting}
                className="text-sm px-3 py-1.5 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
              >
                {resetting ? "Resetting…" : "Reset stakeholder"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// The editable txn list on the shared shell, fed by its own
// `transactions(counterpartyId, month)` query. The two inline editors are injected
// as extraColumns: a per-row CategoryCell (replacing the standard category column,
// hidden here) and the magic-wand "Smooth" button that opens TransformAnchorDialog.
// onChanged refreshes both this list and the page chrome (the replay + flow react
// to attribution/classification edits).
function StakeholderTxns({
  stakeholderId,
  month,
  categories,
  onChrome,
}: {
  stakeholderId: string;
  month: string | undefined;
  categories: ReadonlyArray<CategoryOption>;
  onChrome: () => void;
}) {
  const { refreshing, refresh, queryOptions } = useTxnPanelRefresh();
  const { sort, direction, onSort, sorting, sortVars } = useTxnSort();
  const [openCategory, setOpenCategory] = useState<string | null>(null);
  const [wandTxn, setWandTxn] = useState<{
    md5: string;
    postingDate: string;
    description: string;
    amount: number;
  } | null>(null);

  const data = useLazyLoadQuery<stakeholderDetailPageTxns_Query>(
    txnsQuery,
    {
      counterpartyId: stakeholderId,
      month: month ?? undefined,
      perspectiveId: getPerspectiveId() ?? undefined,
      ...sortVars,
    },
    queryOptions
  );
  const rows = data.transactions.txns;
  const stakeholderOptions: StakeholderOption[] = data.allStakeholders.filter(isRealStakeholder);
  const accountOptions: AccountOption[] = data.bankAccounts.map((a) => ({
    id: a.id,
    name: a.name,
    nickname: a.nickname,
    last4: a.last4,
  }));

  // Re-fetch both the txn list and the page chrome after any edit (inline or bulk),
  // keeping refreshInPlace's anti-clobber refetch for the preloaded→data sync.
  const refreshAll = () => {
    refresh();
    onChrome();
  };

  const { busy, renderBulkActions, dialogs } = useTxnBulkActions({
    enabled: ["setCounterparty", "createRule", "markInternal", "reclassify", "tagNature"],
    perspectiveId: getPerspectiveId(),
    onChanged: refreshAll,
    rows,
    stakeholderOptions,
    accountOptions,
    natureOptions: data.expenseNatures,
  });

  type Row = (typeof rows)[number];

  // The magic-wand: append a MAGIC_WAND_SMOOTH step anchored to this txn (md5 +
  // natural key). The backend rejects an unsmoothable/internal payout, surfaced in
  // the dialog. On success refetch so the chain + preview reflect it.
  const applyWand = (
    txn: { md5: string; postingDate: string; description: string; amount: number },
    args: WandArgs
  ) =>
    new Promise<void>((resolve, reject) => {
      commitMutation<stakeholderDetailPage_TransformWandApplyMutation>(environment, {
        mutation: transformWandApplyMutation,
        variables: {
          input: {
            stakeholderId,
            label: args.label,
            window: args.window,
            method: args.method,
            anchorMd5: txn.md5,
            anchorDate: txn.postingDate,
            anchorAmountCents: Math.round(txn.amount * 100),
            anchorDescription: txn.description,
          },
        },
        onCompleted: (response, errors) => {
          const message =
            errors?.[0]?.message ?? response.transformWandApply?.errors?.[0]?.message;
          if (message) {
            reject(new Error(message));
          } else {
            refresh();
            resolve();
          }
        },
        onError: (err) => reject(err),
      });
    });

  const extraColumns = [
    {
      key: "category",
      header: "Category",
      render: (row: Row) => (
        <CategoryCell
          stakeholderId={stakeholderId}
          categories={categories}
          currentCategoryId={row.categoryNode?.id ?? null}
          open={openCategory === row.md5}
          onOpenChange={(next) => setOpenCategory(next ? row.md5 : null)}
          onAssigned={refresh}
        />
      ),
    },
    {
      key: "recognize",
      header: "Recognize",
      align: "right" as const,
      render: (row: Row) =>
        !row.internal && row.amount < 0 ? (
          <button
            type="button"
            onClick={() =>
              setWandTxn({
                md5: row.md5,
                postingDate: row.postingDate,
                description: row.description,
                amount: row.amount,
              })
            }
            className="text-xs text-emerald-600 hover:text-emerald-800"
          >
            Smooth
          </button>
        ) : (
          <span
            className="text-xs text-gray-300"
            title="Only non-internal outflow costs can be smoothed"
          >
            —
          </span>
        ),
    },
  ];

  const exportParams: Record<string, string> = {
    counterparty_id: stakeholderId,
    sort: sortVars.sort,
    sort_direction: sortVars.sortDirection,
  };
  if (month) exportParams.month = month;
  const perspectiveId = getPerspectiveId();
  if (perspectiveId) exportParams.perspective_id = perspectiveId;

  return (
    <>
      <TxnTablePanel
        mode="inline"
        interaction="editable"
        title="Transactions"
        totalCount={data.transactions.totalCount}
        headerTotalDollars={data.transactions.totalAmount}
        rows={rows}
        emptyLabel={month ? "No transactions in this month." : "No transactions for this counterparty."}
        sort={sort}
        direction={direction}
        onSort={onSort} sorting={sorting}
        exportParams={exportParams}
        busy={busy || refreshing}
        renderBulkActions={renderBulkActions}
        extraColumns={extraColumns}
        hideColumns={["CATEGORY"]}
      />
      {dialogs}
      {wandTxn && (
        <TransformAnchorDialog
          txn={wandTxn}
          onConfirm={(args) => applyWand(wandTxn, args)}
          onClose={() => setWandTxn(null)}
        />
      )}
    </>
  );
}
